import { moment } from '@eventbrite/date';
import { AddressFields } from '@eventbrite/eds-address-fields';
import { Checkbox } from '@eventbrite/eds-checkbox';
import { CheckboxGroup } from '@eventbrite/eds-checkbox-group';
import {
    CheckChunky,
    InfoChunky,
    InfoChunkyRebranding,
} from '@eventbrite/eds-iconography';
import {
    InputField,
    inputFieldConstants,
    SelectField,
} from '@eventbrite/eds-input-field';
import { TYPE_NEUTRAL } from '@eventbrite/eds-notification';
import { Radio } from '@eventbrite/eds-radio';
import {
    GenericLazyString,
    gettext,
    translationPropType,
} from '@eventbrite/i18n';
import range from 'lodash/range';
import PropTypes from 'prop-types';
import React, { ReactElement } from 'react';
import { CannedQuestionName, QuestionType } from '../../api/models/survey';
import {
    PAYMENT_IDENTIFICATION_METHOD,
    PAYMENT_IDENTIFICATION_NUMBER,
} from '../../components/paymentMethodForms/constants';
import {
    IDENTIFICATION_METHOD_CI_FIELD,
    IDENTIFICATION_METHOD_LC_FIELD,
    IDENTIFICATION_METHOD_LE_FIELD,
} from '../../components/paymentMethodForms/IdentificationMethod/IdentificationMethodsARS';
import {
    TAX_IDENTIFIER_TYPE_CNPJ,
    TAX_IDENTIFIER_TYPE_CPF,
} from '../../components/paymentMethodForms/IdentificationMethod/IdentificationMethodsBRL';
import { BUYER_ID } from '../../models/survey';

export const getFieldName = (attendeeId: string, fieldName: string): string =>
    `${attendeeId}.${fieldName}`;

export const MAX_ATTENDEE_AGE = 110;

/**
 * @deprecated Use `CannedQuestionName.prefix` instead.
 */
export const CANNED_QUESTION_PREFIX = CannedQuestionName.prefix;
/**
 * @deprecated Use `CannedQuestionName.firstName` instead.
 */
export const CANNED_QUESTION_FIRST_NAME = CannedQuestionName.firstName;
/**
 * @deprecated Use `CannedQuestionName.lastName` instead.
 */
export const CANNED_QUESTION_LAST_NAME = CannedQuestionName.lastName;
/**
 * @deprecated Use `CannedQuestionName.suffix` instead.
 */
export const CANNED_QUESTION_SUFFIX = CannedQuestionName.suffix;
/**
 * @deprecated Use `CannedQuestionName.email` instead.
 */
export const CANNED_QUESTION_EMAIL_ADDRESS = CannedQuestionName.email;
/**
 * @deprecated Use `CannedQuestionName.homePhone` instead.
 */
export const CANNED_QUESTION_HOME_PHONE = CannedQuestionName.homePhone;
/**
 * @deprecated Use `CannedQuestionName.cellPhone` instead.
 */
export const CANNED_QUESTION_CELL_PHONE = CannedQuestionName.cellPhone;
/**
 * @deprecated Use `CannedQuestionName.company` instead.
 */
export const CANNED_QUESTION_COMPANY = CannedQuestionName.company;
/**
 * @deprecated Use `CannedQuestionName.nameTaxReceipt` instead.
 */
export const CANNED_QUESTION_NAME_TAX_RECEIPT =
    CannedQuestionName.nameTaxReceipt;
/**
 * @deprecated Use `CannedQuestionName.individualTaxAddress` instead.
 */
export const CANNED_QUESTION_INDIVIDUAL_TAX_ADDRESS =
    CannedQuestionName.individualTaxAddress;
/**
 * @deprecated Use `CannedQuestionName.businessTaxAddress` instead.
 */
export const CANNED_QUESTION_BUSINESS_TAX_ADDRESS =
    CannedQuestionName.businessTaxAddress;
/**
 * @deprecated Use `CannedQuestionName.companyTax` instead.
 */
export const CANNED_QUESTION_COMPANY_TAX = CannedQuestionName.companyTax;
/**
 * @deprecated Use `CannedQuestionName.work` instead.
 */
export const CANNED_QUESTION_WORK_ADDRESS = CannedQuestionName.work;
/**
 * @deprecated Use `CannedQuestionName.home` instead.
 */
export const CANNED_QUESTION_HOME_ADDRESS = CannedQuestionName.home;
/**
 * @deprecated Use `CannedQuestionName.ship` instead.
 */
export const CANNED_QUESTION_SHIPPING_ADDRESS = CannedQuestionName.ship;
/**
 * @deprecated Use `CannedQuestionName.jobTitle` instead.
 */
export const CANNED_QUESTION_JOB_TITLE = CannedQuestionName.jobTitle;
/**
 * @deprecated Use `CannedQuestionName.workPhone` instead.
 */
export const CANNED_QUESTION_WORK_PHONE = CannedQuestionName.workPhone;
/**
 * @deprecated Use `CannedQuestionName.website` instead.
 */
export const CANNED_QUESTION_WEBSITE = CannedQuestionName.website;
/**
 * @deprecated Use `CannedQuestionName.blog` instead.
 */
export const CANNED_QUESTION_BLOG = CannedQuestionName.blog;
/**
 * @deprecated Use `CannedQuestionName.sex` instead.
 */
export const CANNED_QUESTION_GENDER = CannedQuestionName.sex;
/**
 * @deprecated Use `CannedQuestionName.birthDate` instead.
 */
export const CANNED_QUESTION_BIRTH_DATE = CannedQuestionName.birthDate;
/**
 * @deprecated Use `CannedQuestionName.age` instead.
 */
export const CANNED_QUESTION_AGE = CannedQuestionName.age;
/**
 * @deprecated Use `CannedQuestionName.cc` instead.
 */
export const CANNED_QUESTION_CREDIT_CARD_INFO = CannedQuestionName.cc;
/**
 * @deprecated Use `CannedQuestionName.bill` instead.
 */
export const CANNED_QUESTION_BILLING_ADDRESS = CannedQuestionName.bill;
/**
 * @deprecated Use `CannedQuestionName.taxRegistrationId` instead.
 */
export const CANNED_QUESTION_TAX_ID = CannedQuestionName.taxRegistrationId;
/**
 * @deprecated Use `CannedQuestionName.isBusiness` instead.
 */
export const CANNED_QUESTION_TAX_INVOICE = CannedQuestionName.isBusiness;
/**
 * @deprecated Use `CannedQuestionName.qstZipCode` instead.
 */
export const CANNED_QUESTION_QST_ZIP_CODE = CannedQuestionName.qstZipCode;
export const CANNED_QUESTION_CONFIRM_EMAIL =
    CannedQuestionName.confirmEmailAddress;

export const SEND_EMAIL_TO_ATTENDEES_FIELD = 'sendEmailToAttendees';
export const EB_MARKETING_OPT_IN_FIELD = 'ebMarketingOptIn';
export const ORG_MARKETING_OPT_IN_FIELD = 'organizationMarketingOptIn';
export const BANCONTACT_FIELDS_GROUP = 'bancontact';
export const PAYMENT_FIELDS_GROUP = 'payment';
export const TOS_CHECKBOX_FIELD = 'tosCheckbox';
export const COPY_BILLING_CONTACT_INFO_FIELD = 'copyBillingInfo';

export const SELECTED_PAYMENT_METHOD_FIELD = 'paymentMethod';
export const PAYPAL_EMAIL_FIELD = 'paypalEmail';
export const BANK_ID_FIELD = 'bankId';
export const IBAN_FIELD = 'iban';
export const IBAN_AGREEMENT_FIELD = 'ibanAgreement';
export const OWNER_NAME_FIELD = 'ownerName';
export const SELECT_ISSUER_FIELD = 'selectIssuer';
export const CARD_HOLDER_FIELD = 'cardholder';
export const ARGENTINA_DNI_FIELD = 'argentinaDni';

const ORGANIZER_MARKETING_OPT_IN_TEXT_WITH_NAME = (name: string) =>
    gettext('Keep me updated on more events and news from %(name)s.', { name });
const ORGANIZER_MARKETING_OPT_IN_TEXT_DEFAULT = gettext(
    'Keep me updated on more events and news from this event organizer.',
);
export const ORGANIZER_MARKETING_OPT_IN_TEXT = (name: string) =>
    name
        ? ORGANIZER_MARKETING_OPT_IN_TEXT_WITH_NAME(name)
        : ORGANIZER_MARKETING_OPT_IN_TEXT_DEFAULT;

export const AMOUNT_FIELD = 'amount';
export const PAY_WITH_VIRTUAL_INCENTIVES_FIELD = 'payWithVirtualIncentives';
export const CREDIT_CARD_NUMBER_FIELD = 'creditCardNumber' as const;
export const CARD_EXPIRATION_DATE_FIELD = 'cardExpirationDate' as const;
export const CARD_POSTAL_CODE_FIELD = 'cardPostalCode' as const;
export const CARD_SECURITY_CODE_FIELD = 'cardSecurityCode' as const;
export const SAVE_CREDIT_CARD_FIELD = 'saveCreditCard';
export const VAULT_ID_FIELD = 'vaultId';

export const PAYMENT_AMOUNT = getFieldName(PAYMENT_FIELDS_GROUP, AMOUNT_FIELD);
export const PAYMENT_PAY_WITH_VIRTUAL_INCENTIVES = getFieldName(
    PAYMENT_FIELDS_GROUP,
    PAY_WITH_VIRTUAL_INCENTIVES_FIELD,
);
export const PAYMENT_CREDIT_CARD_NUMBER = getFieldName(
    PAYMENT_FIELDS_GROUP,
    CREDIT_CARD_NUMBER_FIELD,
);
export const PAYMENT_CARD_EXPIRATION_DATE = getFieldName(
    PAYMENT_FIELDS_GROUP,
    CARD_EXPIRATION_DATE_FIELD,
);
export const PAYMENT_CARD_POSTAL_CODE = getFieldName(
    PAYMENT_FIELDS_GROUP,
    CARD_POSTAL_CODE_FIELD,
);
export const PAYMENT_CARD_SECURITY_CODE = getFieldName(
    PAYMENT_FIELDS_GROUP,
    CARD_SECURITY_CODE_FIELD,
);
export const PAYMENT_SAVE_CREDIT_CARD = getFieldName(
    PAYMENT_FIELDS_GROUP,
    SAVE_CREDIT_CARD_FIELD,
);
export const PAYMENT_VAULT_ID = getFieldName(
    PAYMENT_FIELDS_GROUP,
    VAULT_ID_FIELD,
);

export const BUYER_FIRST_NAME_FIELD = getFieldName(
    BUYER_ID,
    CANNED_QUESTION_FIRST_NAME,
);
export const BUYER_LAST_NAME_FIELD = getFieldName(
    BUYER_ID,
    CANNED_QUESTION_LAST_NAME,
);
export const BUYER_EMAIL_FIELD = getFieldName(
    BUYER_ID,
    CANNED_QUESTION_EMAIL_ADDRESS,
);
export const BUYER_CONFIRM_EMAIL_FIELD = getFieldName(
    BUYER_ID,
    CANNED_QUESTION_CONFIRM_EMAIL,
);
export const BUYER_TAX_INVOICE = getFieldName(
    BUYER_ID,
    CANNED_QUESTION_TAX_INVOICE,
);

export const BUYER_TAX_ID = getFieldName(BUYER_ID, CANNED_QUESTION_TAX_ID);

export const QUESTION_GROUP_BUYER = 'questionsForBuyer';
export const QUESTION_GROUP_ATTENDEES = 'questionsForAttendees';

export const SURVEY_QUESTION_TYPES = [
    QuestionType.address,
    QuestionType.checkbox,
    QuestionType.date,
    QuestionType.dropdown,
    QuestionType.email,
    QuestionType.number,
    QuestionType.paragraph,
    QuestionType.phone,
    QuestionType.radio,
    QuestionType.text,
    QuestionType.url,
    QuestionType.waiver,
];

export const COMPONENT_BY_QUESTION_TYPE = {
    [QuestionType.address]: AddressFields,
    [QuestionType.checkbox]: CheckboxGroup,
    [QuestionType.date]: SelectField,
    [QuestionType.dropdown]: SelectField,
    [QuestionType.email]: InputField,
    [QuestionType.number]: InputField,
    [QuestionType.paragraph]: InputField,
    [QuestionType.phone]: InputField,
    [QuestionType.radio]: Radio,
    [QuestionType.text]: InputField,
    [QuestionType.url]: InputField,
    [QuestionType.waiver]: Checkbox,
};

export const TEXT_INPUT_TYPE_BY_QUESTION_TYPE = {
    [QuestionType.email]: inputFieldConstants.TYPE_EMAIL,
    [QuestionType.number]: inputFieldConstants.TYPE_NUMBER,
    [QuestionType.paragraph]: inputFieldConstants.TYPE_TEXT,
    [QuestionType.phone]: inputFieldConstants.TYPE_TEL,
    [QuestionType.text]: inputFieldConstants.TYPE_TEXT,
    [QuestionType.url]: inputFieldConstants.TYPE_URL,
};

export const PLACEHOLDER_BY_QUESTION_TYPE = {
    [QuestionType.date]: gettext('Choose one'),
    [QuestionType.dropdown]: gettext('Choose one'),
    // Translators: This is a placeholder phone number in a form.
    [QuestionType.phone]: gettext('(555) 555-5555'),
    [QuestionType.url]: 'https://',
};

export const ADDRESS_INPUTS_PREFIX_IDS = {
    [CANNED_QUESTION_SHIPPING_ADDRESS]: CANNED_QUESTION_SHIPPING_ADDRESS,
    [CANNED_QUESTION_HOME_ADDRESS]: CANNED_QUESTION_HOME_ADDRESS,
    [CANNED_QUESTION_WORK_ADDRESS]: CANNED_QUESTION_WORK_ADDRESS,
    [CANNED_QUESTION_INDIVIDUAL_TAX_ADDRESS]:
        CANNED_QUESTION_INDIVIDUAL_TAX_ADDRESS,
    [CANNED_QUESTION_BUSINESS_TAX_ADDRESS]:
        CANNED_QUESTION_BUSINESS_TAX_ADDRESS,
};

export const MONTHS = [
    { value: '1', display: gettext('January') },
    { value: '2', display: gettext('February') },
    { value: '3', display: gettext('March') },
    { value: '4', display: gettext('April') },
    { value: '5', display: gettext('May') },
    { value: '6', display: gettext('June') },
    { value: '7', display: gettext('July') },
    { value: '8', display: gettext('August') },
    { value: '9', display: gettext('September') },
    { value: '10', display: gettext('October') },
    { value: '11', display: gettext('November') },
    { value: '12', display: gettext('December') },
];

/**
 * EDS and Order Service API use slightly different names to identify
 * address subfields. Map those names to each other for use when
 * translating between the API and frontend components.
 */
export const ADDRESS_1_FIELD = 'address1';
export const ADDRESS_2_FIELD = 'address2';
export const CITY_FIELD = 'city';
export const REGION_FIELD = 'region';
export const COUNTRY_FIELD = 'country';
export const POSTAL_FIELD = 'postal';

export const API_ADDRESS_FIELD_TO_EDS_ADDRESS_FIELD = {
    address_1: ADDRESS_1_FIELD,
    address_2: ADDRESS_2_FIELD,
    city: CITY_FIELD,
    region: REGION_FIELD,
    country: COUNTRY_FIELD,
    postal_code: POSTAL_FIELD,
};

/**
 * The checkout page uses different names from the Order Service API
 * to identify the input fields in the Payment Info section. Map those
 * names to each other for use when translating between the API and
 * frontend components.
 */
export const API_PAYMENT_FIELD_TO_CHECKOUT_PAGE_PAYMENT_FIELD = {
    card_number: 'creditCardNumber',
    cvv: 'cardSecurityCode',
    expiration_date: 'cardExpirationDate',
};

export const API_SOURCE_INSTRUMENT_FIELD_TO_CHECKOUT_PAGE_TICKET_BUYER_FIELD = {
    first_name: CannedQuestionName.firstName,
    last_name: CannedQuestionName.lastName,
    email: CannedQuestionName.email,
};

/**
 * Return an array of options for the "day" field of the birthdate
 * question. It returns options for 1-31 regardless of the month,
 * as the backend will reject invalid dates.
 */
const getValidDaysForBirthdate = () =>
    range(1, 32).map((day) => ({
        value: `${day}`,
        display: `${day}`,
    }));

/**
 * Return an array of options for the "year" field of the birthdate
 * question. It returns an option starting with the year 1900 up to the current
 * year.
 */
const getValidYearsForBirthdate = () => {
    const currentYear = moment().year();

    return range(currentYear, currentYear - MAX_ATTENDEE_AGE).map((year) => ({
        value: `${year}`,
        display: `${year}`,
    }));
};

export const YEAR_FIELD = 'year';
export const MONTH_FIELD = 'month';
export const DAY_FIELD = 'day';

export const DATE_CHOICES_MAP = {
    month: MONTHS,
    day: getValidDaysForBirthdate(),
    year: getValidYearsForBirthdate(),
};

export const RENDER_CONDITION_TRIGGER_PROPS = PropTypes.shape({
    choiceId: PropTypes.string,
    parentQuestionId: PropTypes.string,
});

export const SURVEY_QUESTION_CHOICES_SHAPE = PropTypes.arrayOf(
    PropTypes.shape({
        answer: PropTypes.object,
        id: PropTypes.string,
        quantityAvailable: PropTypes.node,
    }).isRequired,
);

export const SURVEY_QUESTION_SHAPE = PropTypes.shape({
    choices: SURVEY_QUESTION_CHOICES_SHAPE.isRequired,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    text: translationPropType.isRequired,
    type: PropTypes.oneOf(SURVEY_QUESTION_TYPES).isRequired,
    waiver: PropTypes.string,
    extraProps: PropTypes.object,
});

export const SURVEY_SHAPE = PropTypes.arrayOf(SURVEY_QUESTION_SHAPE);

export const ATTENDEE_SURVEYS_SHAPE = PropTypes.arrayOf(
    PropTypes.shape({
        id: PropTypes.string.isRequired,
        survey: PropTypes.arrayOf(SURVEY_QUESTION_SHAPE).isRequired,
        ticketClassName: PropTypes.string.isRequired,
    }),
);

export const DISABLED_FIELDS_PROP = PropTypes.shape({
    country: PropTypes.bool,
    address1: PropTypes.bool,
    address2: PropTypes.bool,
    city: PropTypes.bool,
    region: PropTypes.bool,
    postal: PropTypes.bool,
});

export const SURVEY_QUESTION_MAX_LENGTH_MAP = {
    [CANNED_QUESTION_AGE]: 3,
    [CANNED_QUESTION_FIRST_NAME]: 50,
    [CANNED_QUESTION_LAST_NAME]: 50,
    [CANNED_QUESTION_EMAIL_ADDRESS]: 254,
    [CANNED_QUESTION_PREFIX]: 10,
    [CANNED_QUESTION_SUFFIX]: 10,
    [CANNED_QUESTION_JOB_TITLE]: 50,
    [CANNED_QUESTION_COMPANY]: 80,
    [CANNED_QUESTION_GENDER]: 10,
    [CANNED_QUESTION_CELL_PHONE]: 20,
    [CANNED_QUESTION_HOME_PHONE]: 20,
    [CANNED_QUESTION_WORK_PHONE]: 20,
    [CANNED_QUESTION_WEBSITE]: 55,
    [CANNED_QUESTION_BLOG]: 55,
};

export const VALIDATION_ERRORS_MAP = {
    [CANNED_QUESTION_EMAIL_ADDRESS]: gettext(
        'Please enter a valid email address',
    ),
    [CANNED_QUESTION_FIRST_NAME]: gettext('First name is required'),
    [CANNED_QUESTION_LAST_NAME]: gettext('Last name is required'),
    [BANK_ID_FIELD]: gettext('Please select your bank.'),
    [OWNER_NAME_FIELD]: gettext("Account holder's name is required."),
    [IBAN_FIELD]: gettext('A valid IBAN code is required.'),
    [IBAN_AGREEMENT_FIELD]: gettext('Agreement is required.'),
    [PAYMENT_IDENTIFICATION_NUMBER]: gettext(
        'An identification number is required.',
    ),
    [PAYMENT_IDENTIFICATION_METHOD]: gettext(
        'An identification method is required.',
    ),
    [CANNED_QUESTION_HOME_PHONE]: gettext('Please enter a valid phone number.'),
    [SELECT_ISSUER_FIELD]: gettext('An issuer is required.'),
    [CARD_HOLDER_FIELD]: gettext('A cardholder is required.'),
    [CANNED_QUESTION_CONFIRM_EMAIL]: gettext("Email address doesn't match"),
    [TAX_IDENTIFIER_TYPE_CPF]: gettext('Not a valid CPF.'),
    [TAX_IDENTIFIER_TYPE_CNPJ]: gettext('Not a valid CNPJ.'),
    [ARGENTINA_DNI_FIELD]: gettext('Please enter a valid format.'),
    [IDENTIFICATION_METHOD_CI_FIELD]: gettext('Not a valid CI.'),
    [IDENTIFICATION_METHOD_LC_FIELD]: gettext('Not a valid LC.'),
    [IDENTIFICATION_METHOD_LE_FIELD]: gettext('Not a valid LE.'),
    [CANNED_QUESTION_QST_ZIP_CODE]: gettext('Enter a valid postal code'),
    [CANNED_QUESTION_TAX_ID]: gettext('Enter a valid Tax ID'),
    [PAYPAL_EMAIL_FIELD]: gettext(
        'You must provide a valid PayPal account email.',
    ),
    [AMOUNT_FIELD]: gettext('Amount must be greater than 0'),
    [TOS_CHECKBOX_FIELD]: gettext(
        "You must accept Eventbrite's terms of service to complete this purchase.",
    ),
    [CREDIT_CARD_NUMBER_FIELD]: gettext(
        'Please enter a valid Bancontact card number.',
    ),
    [SELECTED_PAYMENT_METHOD_FIELD]: gettext('Please select a payment method.'),
};

export const DEFAULT_VALIDATION_ERROR = gettext(
    'Please provide a valid value for this field.',
);

export const INTERNAL_SERVER_ERROR = 'INTERNAL_ERROR';
export const ARGUMENTS_ERROR = 'ARGUMENTS_ERROR';
export const RETRY_ERROR = 'RETRY_ERROR';

// This const is use in partiallyIndexedQuestions to make a index on survey question.
export const PARENT_FIELD_MULTIPLIER = 100;

export const DefaultMobileCallBack = () => window.history.back();

export const COPY_DATA_FROM_ATTENDEE_SELECTOR_PREFIX =
    'copy-data-from-attendee-selector-';

/**
 * Checkout Order Eligibility Disqualifications
 */

export const ORDER_DISQUALIFICATIONS = {
    BELOW_MINIMUM_QUANTITY: 'below_qty_minimum',
    EXCEED_MAXIMUM_QUANTITY: 'exceed_qty_maximum',
    UNKNOWN: 'unknown',
};

export const ORDER_DISQUALIFICATIONS_FIELD_MAP = {
    BELOW_MINIMUM_QUANTITY: 'minimumQuantity',
};

export enum TaxExemptionStatus {
    init = 'INIT',
    verifying = 'VERIFYING',
    invalidTaxId = 'INVALID_TAX_ID',
    validTaxIdWithoutTaxExemption = 'VALID_TAX_ID_NO_TAX_EXEMPTION',
    validTaxIdAndTaxExemption = 'VALID_TAX_ID_AND_TAX_EXEMPTION',
}

export const TAX_EXEMPTION_DATA_SHAPE = PropTypes.shape({
    status: PropTypes.oneOf([
        TaxExemptionStatus.init,
        TaxExemptionStatus.verifying,
        TaxExemptionStatus.invalidTaxId,
        TaxExemptionStatus.validTaxIdWithoutTaxExemption,
        TaxExemptionStatus.validTaxIdAndTaxExemption,
    ]),
    hasTaxCountryExemptions: PropTypes.bool.isRequired,
});

export const quebecQSTNotificationSettingsByStatus: Record<
    TaxExemptionStatus,
    {
        label: GenericLazyString;
        iconType?: ReactElement;
        type: string;
        hasCloseButton: boolean;
    } | null
> = {
    [TaxExemptionStatus.init]: null,
    [TaxExemptionStatus.verifying]: {
        label: gettext('Verifying...'),
        type: TYPE_NEUTRAL,
        hasCloseButton: false,
    },
    [TaxExemptionStatus.validTaxIdAndTaxExemption]: {
        label: gettext("You're exempt from paying QST taxes on this order"),
        iconType: <CheckChunky />,
        type: TYPE_NEUTRAL,
        hasCloseButton: true,
    },
    [TaxExemptionStatus.invalidTaxId]: {
        label: gettext(
            "We couldn't verify your tax exempt status for this order.",
        ),
        iconType: <InfoChunky />,
        type: TYPE_NEUTRAL,
        hasCloseButton: false,
    },
    [TaxExemptionStatus.validTaxIdWithoutTaxExemption]: {
        label: gettext(
            "We couldn't verify your tax exempt status for this order.",
        ),
        iconType: <InfoChunky />,
        type: TYPE_NEUTRAL,
        hasCloseButton: false,
    },
};

export const quebecQSTNotificationSettingsByStatusRebranding: Record<
    TaxExemptionStatus,
    {
        label: GenericLazyString;
        iconType?: ReactElement;
        type: string;
        hasCloseButton: boolean;
        iconColor?: string;
    } | null
> = {
    [TaxExemptionStatus.init]: null,
    [TaxExemptionStatus.verifying]: {
        label: gettext('Verifying...'),
        type: TYPE_NEUTRAL,
        hasCloseButton: false,
    },
    [TaxExemptionStatus.validTaxIdAndTaxExemption]: {
        label: gettext("You're exempt from paying QST taxes on this order"),
        iconType: <CheckChunky />,
        type: TYPE_NEUTRAL,
        hasCloseButton: true,
        iconColor: 'rebranding-blue',
    },
    [TaxExemptionStatus.invalidTaxId]: {
        label: gettext(
            "We couldn't verify your tax exempt status for this order.",
        ),
        iconType: <InfoChunkyRebranding />,
        type: TYPE_NEUTRAL,
        hasCloseButton: false,
    },
    [TaxExemptionStatus.validTaxIdWithoutTaxExemption]: {
        label: gettext(
            "We couldn't verify your tax exempt status for this order.",
        ),
        iconType: <InfoChunkyRebranding />,
        type: TYPE_NEUTRAL,
        hasCloseButton: false,
    },
};
